import { useEffect, useRef, useState } from "react";
import { type ProductMedia } from "@/lib/supabase";

type Props = {
  media: ProductMedia[];
  alt: string;
  onOpenLightbox?: (index: number) => void;
};

export default function ProductGallery({ media, alt, onOpenLightbox }: Props) {
  const safe = media.length > 0 ? media : [];

  // Mobile carousel state
  const [active, setActive] = useState(0);
  const carouselRef = useRef<HTMLDivElement | null>(null);

  // Track active slide on scroll (mobile)
  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    const onScroll = () => {
      const i = Math.round(el.scrollLeft / el.clientWidth);
      setActive(i);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToSlide = (i: number) => {
    const el = carouselRef.current;
    if (!el) return;
    el.scrollTo({ left: i * el.clientWidth, behavior: "smooth" });
  };

  // Hover-zoom for desktop large images
  const [zoom, setZoom] = useState<{ idx: number; x: number; y: number } | null>(null);

  if (safe.length === 0) {
    return (
      <div className="aspect-[4/5] rounded-3xl bg-background-100 flex items-center justify-center text-foreground-500">
        <i className="ri-image-line text-5xl"></i>
      </div>
    );
  }

  return (
    <>
      {/* Mobile: swipeable carousel */}
      <div className="lg:hidden">
        <div
          ref={carouselRef}
          className="flex overflow-x-auto snap-x snap-mandatory rounded-2xl no-scrollbar"
        >
          {safe.map((m, i) => (
            <button
              key={i}
              type="button"
              onClick={() => onOpenLightbox?.(i)}
              className="snap-center shrink-0 w-full aspect-[4/5] bg-background-100 relative overflow-hidden cursor-zoom-in"
              aria-label={`Image ${i + 1}, click to zoom`}
            >
              <img
                src={m.url}
                alt={`${alt} — view ${i + 1}`}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
            </button>
          ))}
        </div>
        {safe.length > 1 && (
          <div className="mt-3 flex items-center justify-center gap-1.5">
            {safe.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => scrollToSlide(i)}
                className={`h-1.5 rounded-full transition-all cursor-pointer ${
                  active === i ? "w-6 bg-foreground-950" : "w-1.5 bg-foreground-300"
                }`}
                aria-label={`Go to image ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Desktop: stacked large images with hover zoom */}
      <div className="hidden lg:flex flex-col gap-4">
        {safe.map((m, i) => (
          <div
            key={i}
            className="relative overflow-hidden rounded-2xl bg-background-100 group cursor-zoom-in"
            onMouseMove={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              const x = ((e.clientX - r.left) / r.width) * 100;
              const y = ((e.clientY - r.top) / r.height) * 100;
              setZoom({ idx: i, x, y });
            }}
            onMouseLeave={() => setZoom((z) => (z?.idx === i ? null : z))}
            onClick={() => onOpenLightbox?.(i)}
          >
            <div className={i === 0 ? "aspect-[4/5]" : "aspect-[4/5]"}>
              <img
                src={m.url}
                alt={`${alt} — view ${i + 1}`}
                className="w-full h-full object-cover object-top transition-transform duration-700 ease-out"
                style={
                  zoom?.idx === i
                    ? {
                        transformOrigin: `${zoom.x}% ${zoom.y}%`,
                        transform: "scale(1.6)",
                      }
                    : undefined
                }
              />
            </div>

            {/* Index pill */}
            <span className="absolute top-4 left-4 inline-flex items-center px-2.5 h-6 rounded-full bg-background-50/90 backdrop-blur text-[10px] tracking-widest uppercase text-foreground-950">
              {String(i + 1).padStart(2, "0")} / {String(safe.length).padStart(2, "0")}
            </span>

            {/* Expand icon */}
            <span className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-background-50/90 backdrop-blur text-foreground-950 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <i className="ri-fullscreen-line text-base"></i>
            </span>
          </div>
        ))}
      </div>
    </>
  );
}